/**
 * Sprint GT-DATA-PLANE-OPERATOR-SURFACE-08 — admin client for the
 * default-OFF evidence-bundle API.
 *
 * Source-guard contract (`source-guards.test.ts`):
 *   - this module imports only `apiSend` + `ApiError` from
 *     `../../lib/api.js`;
 *   - the ONLY paths referenced are
 *     `/v1/admin/data-plane/evidence-bundles/verify` and
 *     `/v1/admin/data-plane/evidence-bundles/render`;
 *   - no `fetch` / `axios` / `XMLHttpRequest`;
 *   - no fixture import;
 *   - no `services/**` import.
 */

import { ApiError, apiSend } from "../../lib/api.js";
import type { RenderApiResponse, ReviewError, VerifyApiResponse } from "./types.js";

const VERIFY_PATH = "/v1/admin/data-plane/evidence-bundles/verify";
const RENDER_PATH = "/v1/admin/data-plane/evidence-bundles/render";

/**
 * POST the caller-supplied bundle to the verifier route. Returns
 * either a successful API response or a closed-shape `ReviewError`.
 */
export async function verifyEvidenceBundle(
  bundle: unknown,
  signal?: AbortSignal,
): Promise<{ ok: true; data: VerifyApiResponse } | { ok: false; error: ReviewError }> {
  try {
    const data = await apiSend<VerifyApiResponse>("POST", VERIFY_PATH, bundle, signal);
    return { ok: true, data };
  } catch (err) {
    return { ok: false, error: mapError(err) };
  }
}

/**
 * POST the caller-supplied bundle to the renderer route. Returns
 * either a successful API response or a closed-shape `ReviewError`.
 */
export async function renderEvidenceBundle(
  bundle: unknown,
  signal?: AbortSignal,
): Promise<{ ok: true; data: RenderApiResponse } | { ok: false; error: ReviewError }> {
  try {
    const data = await apiSend<RenderApiResponse>("POST", RENDER_PATH, bundle, signal);
    return { ok: true, data };
  } catch (err) {
    return { ok: false, error: mapError(err) };
  }
}

function mapError(err: unknown): ReviewError {
  if (err instanceof ApiError) {
    if (err.status === 404) return { kind: "api_disabled", requestId: err.requestId };
    if (err.status === 401) return { kind: "unauthorized", requestId: err.requestId };
    if (err.status === 403) return { kind: "forbidden", requestId: err.requestId };
    if (err.status === 422) return { kind: "malformed_bundle", requestId: err.requestId };
    if (err.status === 429) return { kind: "rate_limited", requestId: err.requestId };
    return { kind: "unknown", requestId: err.requestId };
  }
  return { kind: "unknown" };
}
